import { placePassages } from './place-passages'
import { readPassage, readVisibleText, type DomLocation } from './visible-text'

export const kHighlightClassName = 'ref-highlight'

const kStructuralParents = new Set(['TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'UL', 'OL', 'DL', 'DIV', 'SECTION', 'BLOCKQUOTE'])

/** Highlight each passage in `root` once, wrapping its visible text in `<mark class="ref-highlight">`. */
export function highlightPassages(root: Node, passages: string[]): void {
  const text = readVisibleText(root)
  const placements = placePassages(
    text,
    passages.map((passage) => readPassage(passage))
  )
  const paintedUnits = new Map<Text, boolean[]>()
  for (const placement of placements) {
    if (!placement) continue
    for (let index = placement.first; index <= placement.last; index++) markPainted(paintedUnits, text.chars[index].location)
  }
  paintedUnits.forEach((painted, node) => wrapPaintedUnits(node, painted))
}

function markPainted(paintedUnits: Map<Text, boolean[]>, location: DomLocation | null): void {
  if (!location) return
  const { node, offset } = location
  let painted = paintedUnits.get(node)
  if (!painted) {
    painted = new Array<boolean>(node.data.length).fill(false)
    paintedUnits.set(node, painted)
  }
  // Painting covers the whole character, including both halves of a surrogate pair.
  const length = [...node.data.slice(offset, offset + 2)][0]?.length ?? 1
  for (let unit = offset; unit < offset + length; unit++) painted[unit] = true
}

/** Replace a text node with plain and `<mark>` pieces. Whitespace between structural blocks stays unwrapped. */
function wrapPaintedUnits(node: Text, painted: boolean[]): void {
  const parent = node.parentNode
  if (!parent) return
  if (!node.data.trim() && parent instanceof Element && kStructuralParents.has(parent.tagName)) return

  const document = node.ownerDocument
  const fragment = document.createDocumentFragment()
  let runStart = 0
  for (let unit = 1; unit <= node.data.length; unit++) {
    if (unit < node.data.length && painted[unit] === painted[runStart]) continue
    const piece = document.createTextNode(node.data.slice(runStart, unit))
    if (painted[runStart]) {
      const mark = document.createElement('mark')
      mark.className = kHighlightClassName
      mark.append(piece)
      fragment.append(mark)
    } else {
      fragment.append(piece)
    }
    runStart = unit
  }
  parent.replaceChild(fragment, node)
}
